import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Square, Cloud, ArrowUpRight, Highlighter, Underline,
  Trash2, Save, GripVertical, ChevronLeft,
  ChevronRight, Loader2, MousePointer, Palette, Undo2,
  ZoomIn, ZoomOut, Hand, CheckCircle, Type, Check, X, Maximize, Flag, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api/subbuilder`;

// ─── Toolbar ───────────────────────────────────────────
// Custom icon: X inside a circle
const CircleXIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="10" fill="#fde047" fillOpacity="0.5" stroke="currentColor" />
    <line x1="8" y1="8" x2="16" y2="16" stroke="#b91c1c" strokeWidth="2.5" />
    <line x1="16" y1="8" x2="8" y2="16" stroke="#b91c1c" strokeWidth="2.5" />
  </svg>
);

// Custom icon: Large blue X for crossing out
const BigXIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
    fill="none" stroke="#2563eb" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="4" y1="4" x2="20" y2="20" />
    <line x1="20" y1="4" x2="4" y2="20" />
  </svg>
);

const TOOLS = [
  { id: 'select', icon: MousePointer, label: 'Select' },
  { id: 'hand', icon: Hand, label: 'Pan / Move' },
  { id: 'text', icon: Type, label: 'Text' },
  { id: 'checkmark', icon: Check, label: 'Checkmark' },
  { id: 'xmark', icon: X, label: 'X Mark' },
  { id: 'box', icon: Square, label: 'Box' },
  { id: 'cloud', icon: Cloud, label: 'Cloud' },
  { id: 'arrow', icon: ArrowUpRight, label: 'Arrow' },
  { id: 'highlight', icon: Highlighter, label: 'Highlight' },
  { id: 'underline', icon: Underline, label: 'Underline' },
  { id: 'circlexmark', icon: CircleXIcon, label: 'Circle X' },
  { id: 'bigx', icon: BigXIcon, label: 'Cross Out' },
  { id: 'structhighlight', icon: Highlighter, label: 'Structure Required' },
  { id: 'flag', icon: Flag, label: 'Flag Markup' },
];

const FLAG_PRESETS = [
  { text: 'WALL OPENING REQUIRED', bg: '#dc2626', border: '#991b1b', fg: '#ffffff' },
  { text: 'COUNTERTOP OPENING REQUIRED', bg: '#dc2626', border: '#991b1b', fg: '#ffffff' },
  { text: 'BLOCKING REQUIRED', bg: '#dc2626', border: '#991b1b', fg: '#ffffff' },
  { text: 'CONFIRM COLOR', bg: '#facc15', border: '#ca8a04', fg: '#000000' },
  { text: 'SELECT COLOR', bg: '#facc15', border: '#ca8a04', fg: '#000000' },
  { text: 'CONFIRM DOOR SWING', bg: '#facc15', border: '#ca8a04', fg: '#000000' },
  { text: 'FURNISHED ONLY', bg: '#22c55e', border: '#16a34a', fg: '#ffffff' },
  { text: 'FIELD DIMENSIONS REQUIRED', bg: '#f97316', border: '#ea580c', fg: '#ffffff' },
  { text: 'ALL DIMENSIONS TO BE FIELD VERIFIED', bg: '#2563eb', border: '#1d4ed8', fg: '#ffffff' },
];

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32];

const COLORS = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#FACC15', '#8B5CF6', '#000000'];

// ─── Bubbly Cloud SVG path helper ────────────────────────
function cloudPath(x, y, w, h) {
  if (w < 2 || h < 2) return '';
  const cx = x + w / 2;
  const cy = y + h / 2;
  const rx = w / 2;
  const ry = h / 2;
  // Fewer, bigger bumps = more cloud-like
  const numBumps = Math.max(6, Math.round((w + h) / 32));
  const baseBump = Math.min(w, h) * 0.35;
  let d = '';
  for (let i = 0; i < numBumps; i++) {
    const a1 = (i / numBumps) * Math.PI * 2;
    const a2 = ((i + 1) / numBumps) * Math.PI * 2;
    const px1 = cx + rx * Math.cos(a1);
    const py1 = cy + ry * Math.sin(a1);
    const px2 = cx + rx * Math.cos(a2);
    const py2 = cy + ry * Math.sin(a2);
    // Alternate bump sizes for organic feel
    const vary = (i % 2 === 0) ? baseBump : baseBump * 0.65;
    const midAngle = (a1 + a2) / 2;
    const cpx = cx + (rx + vary) * Math.cos(midAngle);
    const cpy = cy + (ry + vary) * Math.sin(midAngle);
    if (i === 0) d += `M ${px1} ${py1}`;
    d += ` Q ${cpx} ${cpy} ${px2} ${py2}`;
  }
  d += ' Z';
  return d;
}

// ─── Main Component ─────────────────────────────────────
export default function AnnotateStep({ project, vendorDocs, onComplete }) {
  const [submittals, setSubmittals] = useState([]);
  const [selectedSubmittal, setSelectedSubmittal] = useState(null);
  const [loading, setLoading] = useState(true);

  // PDF state
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [scale, setScale] = useState(1.0);

  // Annotation state
  const [tool, setTool] = useState('select');
  const [color, setColor] = useState('#3B82F6');
  const [fontSize, setFontSize] = useState(12);
  const [activeFlag, setActiveFlag] = useState(FLAG_PRESETS[0]);
  const [showFlagMenu, setShowFlagMenu] = useState(false);
  const [customFlagText, setCustomFlagText] = useState('');
  const flagMenuRef = useRef(null);
  const [structHighlightWidth, setStructHighlightWidth] = useState(12);
  const [dragInfo, setDragInfo] = useState(null); // { pageKey, idx, startX, startY, origX, origY }
  const [annotations, setAnnotations] = useState({}); // { pages: {}, page_order: [], deleted_pages: [] }
  const [drawing, setDrawing] = useState(null); // current draw-in-progress
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [textInput, setTextInput] = useState(null); // { x, y, value }

  // Page management
  const [pageOrder, setPageOrder] = useState([]);
  const [deletedPages, setDeletedPages] = useState(new Set());
  const [dragIdx, setDragIdx] = useState(null);

  const svgRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Pan state for hand tool
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // ─── Load submittals ────────────────────────────────
  // Close flag dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (flagMenuRef.current && !flagMenuRef.current.contains(e.target)) setShowFlagMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!project?.id) return;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${API}/projects/${project.id}/project-submittals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const subs = (res.data?.submittals || res.data || []).filter(s => s.package_url);
        setSubmittals(subs);
        if (subs.length > 0) {
          setSelectedSubmittal(subs[0]);
        }
      } catch (e) {
        console.error('Failed to load submittals:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project?.id]);

  // ─── Load PDF + annotations when submittal changes ──
  useEffect(() => {
    if (!selectedSubmittal?.id || !project?.id) return;

    const loadPdfAndAnnotations = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        // Get the full package URL (base + drawings merged)
        const pkgRes = await axios.get(
          `${API}/projects/${project.id}/project-submittals/${selectedSubmittal.id}/full-package-url`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        let url = pkgRes.data?.url || selectedSubmittal.package_url || '';
        if (url && !url.startsWith('http')) url = `${BACKEND_URL}${url.startsWith('/') ? url : '/' + url}`;
        setPdfUrl(url);
        setCurrentPage(0);
        setScale(1.0);

        // Load saved annotations
        const annRes = await axios.get(
          `${API}/projects/${project.id}/project-submittals/${selectedSubmittal.id}/annotations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = annRes.data?.annotations || {};
        setAnnotations(data);
        setPageOrder(data.page_order || []);
        setDeletedPages(new Set(data.deleted_pages || []));
      } catch (e) {
        console.error('Error loading PDF/annotations:', e);
        // Fallback to direct package_url
        let url = selectedSubmittal.package_url || '';
        if (url && !url.startsWith('http')) url = `${BACKEND_URL}${url.startsWith('/') ? url : '/' + url}`;
        setPdfUrl(url);
        setAnnotations({ pages: {}, page_order: [], deleted_pages: [] });
        setPageOrder([]);
        setDeletedPages(new Set());
      }
    };
    loadPdfAndAnnotations();
  }, [selectedSubmittal, project?.id]);

  // Init page order when PDF loads
  const onDocLoad = useCallback(({ numPages: np }) => {
    setNumPages(np);
    setPageOrder(prev => prev.length === np ? prev : Array.from({ length: np }, (_, i) => i));
  }, []);

  // ─── Auto-save (debounced) ──────────────────────────
  const autoSave = useCallback((anns, order, deleted) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      if (!selectedSubmittal || !project) return;
      setSaving(true);
      try {
        const token = localStorage.getItem('auth_token');
        await axios.put(
          `${API}/projects/${project.id}/project-submittals/${selectedSubmittal.id}/annotations`,
          { annotations: { ...anns, page_order: order, deleted_pages: [...deleted] } },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      } catch (e) {
        console.error('Auto-save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 800);
  }, [selectedSubmittal, project]);

  // ─── Pan handlers for hand tool ─────────────────────
  const handlePanStart = (e) => {
    if (tool !== 'hand') return;
    e.preventDefault();
    setIsPanning(true);
    const container = containerRef.current;
    panStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container?.scrollLeft || 0,
      scrollTop: container?.scrollTop || 0,
    };
  };

  const handlePanMove = useCallback((e) => {
    if (!isPanning || tool !== 'hand') return;
    const container = containerRef.current;
    if (!container) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    container.scrollLeft = panStart.current.scrollLeft - dx;
    container.scrollTop = panStart.current.scrollTop - dy;
  }, [isPanning, tool]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  useEffect(() => {
    if (tool !== 'hand') return;
    window.addEventListener('mousemove', handlePanMove);
    window.addEventListener('mouseup', handlePanEnd);
    return () => {
      window.removeEventListener('mousemove', handlePanMove);
      window.removeEventListener('mouseup', handlePanEnd);
    };
  }, [tool, handlePanMove, handlePanEnd]);

  // ─── Keyboard delete for selected annotation ──────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selectedAnnotation) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      handleDeleteAnnotation(selectedAnnotation.pageKey, selectedAnnotation.idx);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
     
  }, [selectedAnnotation, annotations]);

  // ─── Window-level drag listeners for reliable annotation moving ──
  useEffect(() => {
    if (!dragInfo) return;
    const onMove = (e) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const dx = px - dragInfo.startX;
      const dy = py - dragInfo.startY;
      const orig = dragInfo.origAnn;
      const moved = { ...orig, x: (orig.x || 0) + dx, y: (orig.y || 0) + dy };
      if ('x2' in orig) moved.x2 = (orig.x2 || 0) + dx;
      if ('y2' in orig) moved.y2 = (orig.y2 || 0) + dy;
      setAnnotations(prev => {
        const newAnns = { ...prev, pages: { ...prev.pages } };
        const pa = [...(newAnns.pages?.[dragInfo.pageKey] || [])];
        pa[dragInfo.idx] = moved;
        newAnns.pages[dragInfo.pageKey] = pa;
        return newAnns;
      });
    };
    const onUp = () => {
      // Only save history/autoSave if annotation actually moved
      setAnnotations(current => {
        const cur = current.pages?.[dragInfo.pageKey]?.[dragInfo.idx];
        const orig = dragInfo.origAnn;
        if (cur && (cur.x !== orig.x || cur.y !== orig.y)) {
          setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
          autoSave(current, pageOrder, deletedPages);
        }
        return current;
      });
      setDragInfo(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
     
  }, [dragInfo]);

  // ─── Drawing handlers ──────────────────────────────
  const getPos = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // ─── Hit-test any annotation type ─────────────────
  const hitTestAnnotation = (pos, ann) => {
    const pad = 10;
    switch (ann.type) {
      case 'checkmark':
      case 'xmark':
      case 'circlexmark':
        return Math.abs(pos.x - ann.x) < 15 && Math.abs(pos.y - ann.y) < 15;
      case 'bigx': {
        const arm = 24 * (ann.scale || 1);
        return Math.abs(pos.x - ann.x) < arm + pad && Math.abs(pos.y - ann.y) < arm + pad;
      }
      case 'text': {
        const tw = (ann.text?.length || 5) * (ann.fontSize || 14) * 0.6;
        const th = (ann.fontSize || 14) * 1.4;
        return pos.x >= ann.x - pad && pos.x <= ann.x + tw + pad &&
               pos.y >= ann.y - pad && pos.y <= ann.y + th + pad;
      }
      case 'flag':
      case 'wallopening': {
        const label = ann.text || 'FLAG';
        const flagW = label.length * 8.2 + 28;
        const flagH = 28;
        return pos.x >= ann.x - pad && pos.x <= ann.x + flagW + pad &&
               pos.y >= ann.y - flagH - pad && pos.y <= ann.y + 12 + pad;
      }
      case 'box':
      case 'cloud':
      case 'highlight':
        return pos.x >= ann.x - pad && pos.x <= ann.x + Math.abs(ann.width || 0) + pad &&
               pos.y >= ann.y - pad && pos.y <= ann.y + Math.abs(ann.height || 0) + pad;
      case 'underline': {
        const ly = ann.y + Math.abs(ann.height || 0);
        return pos.x >= ann.x - pad && pos.x <= ann.x + Math.abs(ann.width || 0) + pad &&
               Math.abs(pos.y - ly) < pad + 3;
      }
      case 'structhighlight': {
        const sy = ann.y + (ann.height || 0) / 2;
        const sw = Math.abs(ann.width || 0);
        const sh = Math.abs(ann.height || ann.strokeWidth || 12) / 2;
        return pos.x >= ann.x - pad && pos.x <= ann.x + sw + pad &&
               Math.abs(pos.y - sy) < sh + pad;
      }
      case 'arrow': {
        const adx = (ann.x2 || ann.x) - ann.x;
        const ady = (ann.y2 || ann.y) - ann.y;
        const lenSq = adx * adx + ady * ady;
        if (lenSq === 0) return Math.hypot(pos.x - ann.x, pos.y - ann.y) < pad;
        const t = Math.max(0, Math.min(1, ((pos.x - ann.x) * adx + (pos.y - ann.y) * ady) / lenSq));
        const dist = Math.hypot(pos.x - (ann.x + t * adx), pos.y - (ann.y + t * ady));
        return dist < pad + 5;
      }
      default:
        return false;
    }
  };

  const handleMouseDown = (e) => {
    // ─── Universal annotation selection & drag (select / hand) ───
    if (tool === 'select' || tool === 'hand') {
      const pos = getPos(e);
      const vp = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
      const pageKey = String(vp);
      const pageAnns = annotations?.pages?.[pageKey] || [];
      for (let i = pageAnns.length - 1; i >= 0; i--) {
        if (hitTestAnnotation(pos, pageAnns[i])) {
          setSelectedAnnotation({ pageKey, idx: i });
          setDragInfo({ pageKey, idx: i, startX: pos.x, startY: pos.y, origAnn: { ...pageAnns[i] } });
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      setSelectedAnnotation(null);
      if (tool === 'hand') return; // let event bubble to container for panning
      return;
    }
    if (tool === 'checkmark' || tool === 'xmark' || tool === 'circlexmark' || tool === 'bigx') {
      const pos = getPos(e);
      const svgRect = svgRef.current?.getBoundingClientRect();
      const visiblePage = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
      const pageKey = String(visiblePage);
      const newAnns = { ...annotations };
      if (!newAnns.pages) newAnns.pages = {};
      if (!newAnns.pages[pageKey]) newAnns.pages[pageKey] = [];
      newAnns.pages[pageKey] = [...newAnns.pages[pageKey], {
        type: tool,
        x: pos.x,
        y: pos.y,
        color,
        canvasWidth: svgRect?.width || 800,
        canvasHeight: svgRect?.height || 1000,
      }];
      setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
      setAnnotations(newAnns);
      autoSave(newAnns, pageOrder, deletedPages);
      return;
    }
    if (tool === 'flag') {
      const pos = getPos(e);
      const svgRect = svgRef.current?.getBoundingClientRect();
      const visiblePage = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
      const pageKey = String(visiblePage);
      const newAnns = { ...annotations };
      if (!newAnns.pages) newAnns.pages = {};
      if (!newAnns.pages[pageKey]) newAnns.pages[pageKey] = [];
      const flagLabel = typeof activeFlag === 'string' ? activeFlag : activeFlag.text;
      const flagBg = typeof activeFlag === 'string' ? '#dc2626' : activeFlag.bg;
      const flagBorder = typeof activeFlag === 'string' ? '#991b1b' : activeFlag.border;
      const flagFg = typeof activeFlag === 'string' ? '#ffffff' : activeFlag.fg;
      const charW = 8.2, padX = 14;
      const flagW = flagLabel.length * charW + padX * 2;
      newAnns.pages[pageKey] = [...newAnns.pages[pageKey], {
        type: 'flag',
        text: flagLabel,
        bg: flagBg,
        border: flagBorder,
        fg: flagFg,
        x: pos.x,
        y: pos.y,
        canvasWidth: svgRect?.width || 800,
        canvasHeight: svgRect?.height || 1000,
      }];
      setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
      setAnnotations(newAnns);
      autoSave(newAnns, pageOrder, deletedPages);
      return;
    }
    if (tool === 'text') {
      // If already typing, commit current text first
      if (textInput) {
        commitText(textInput.value);
        return;
      }
      const pos = getPos(e);
      e.stopPropagation();
      e.preventDefault();
      const svgRect = svgRef.current?.getBoundingClientRect();
      setTextInput({
        x: pos.x, y: pos.y, value: '',
        canvasWidth: svgRect?.width || 800,
        canvasHeight: svgRect?.height || 1000,
      });
      return;
    }
    const pos = getPos(e);
    const svgRect = svgRef.current?.getBoundingClientRect();
    setDrawing({
      type: tool, color: tool === 'structhighlight' ? '#4ade80' : color,
      strokeWidth: tool === 'structhighlight' ? structHighlightWidth : 2,
      x: pos.x, y: pos.y,
      x2: pos.x, y2: pos.y,
      width: 0, height: 0,
      canvasWidth: svgRect?.width || 800,
      canvasHeight: svgRect?.height || 1000,
    });
  };

  const handleMouseMove = (e) => {
    if (dragInfo) return; // Drag handled by window listener
    if (!drawing) return;
    const pos = getPos(e);
    setDrawing(prev => ({
      ...prev,
      width: pos.x - prev.x,
      height: pos.y - prev.y,
      x2: pos.x, y2: pos.y,
    }));
  };

  const handleMouseUp = () => {
    if (dragInfo) return; // Drag handled by window listener
    if (!drawing) return;
    const minSize = 5;
    const w = Math.abs(drawing.width);
    const h = Math.abs(drawing.height);
    if ((drawing.type === 'arrow' && Math.sqrt(w * w + h * h) < minSize) ||
        (drawing.type !== 'arrow' && w < minSize && h < minSize)) {
      setDrawing(null);
      return;
    }
    // Normalize negative width/height for box/cloud/highlight
    const normalized = { ...drawing };
    if (drawing.type !== 'arrow') {
      if (drawing.width < 0) { normalized.x = drawing.x + drawing.width; normalized.width = Math.abs(drawing.width); }
      if (drawing.height < 0) { normalized.y = drawing.y + drawing.height; normalized.height = Math.abs(drawing.height); }
    }

    const visiblePage = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
    const pageKey = String(visiblePage);
    const newAnns = { ...annotations };
    if (!newAnns.pages) newAnns.pages = {};
    if (!newAnns.pages[pageKey]) newAnns.pages[pageKey] = [];
    newAnns.pages[pageKey] = [...newAnns.pages[pageKey], normalized];

    setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
    setAnnotations(newAnns);
    setDrawing(null);
    autoSave(newAnns, pageOrder, deletedPages);
  };

  // ─── Undo ────────────────────────────────────────
  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setAnnotations(prev.annotations);
    autoSave(prev.annotations, pageOrder, deletedPages);
  };

  // ─── Commit text annotation ────────────────────────
  const commitText = (value) => {
    if (!value || !value.trim() || !textInput) { setTextInput(null); return; }
    const visiblePage = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
    const pageKey = String(visiblePage);
    const newAnns = { ...annotations };
    if (!newAnns.pages) newAnns.pages = {};
    if (!newAnns.pages[pageKey]) newAnns.pages[pageKey] = [];
    newAnns.pages[pageKey] = [...newAnns.pages[pageKey], {
      type: 'text',
      x: textInput.x,
      y: textInput.y,
      text: value.trim(),
      color,
      fontSize,
      fontWeight: 'bold',
      fontFamily: 'Times New Roman',
      canvasWidth: textInput.canvasWidth,
      canvasHeight: textInput.canvasHeight,
    }];
    setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
    setAnnotations(newAnns);
    setTextInput(null);
    autoSave(newAnns, pageOrder, deletedPages);
  };

  // ─── Delete annotation ───────────────────────────
  const handleDeleteAnnotation = (pageKey, annIdx) => {
    const newAnns = { ...annotations, pages: { ...annotations.pages } };
    newAnns.pages[pageKey] = [...(newAnns.pages[pageKey] || [])];
    newAnns.pages[pageKey].splice(annIdx, 1);
    setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
    setAnnotations(newAnns);
    setSelectedAnnotation(null);
    autoSave(newAnns, pageOrder, deletedPages);
  };

  // ─── Double-click to edit text ────────────────────
  const commitEdit = (value) => {
    if (!editingAnnotation) return;
    const { pageKey, idx } = editingAnnotation;
    const newAnns = { ...annotations };
    const pageAnns = [...(newAnns.pages?.[pageKey] || [])];
    if (pageAnns[idx]) {
      const trimmed = (value || '').trim();
      if (trimmed) {
        pageAnns[idx] = { ...pageAnns[idx], text: trimmed };
        newAnns.pages = { ...newAnns.pages, [pageKey]: pageAnns };
        setHistory(prev => [...prev, { annotations: JSON.parse(JSON.stringify(annotations)) }]);
        setAnnotations(newAnns);
        autoSave(newAnns, pageOrder, deletedPages);
      }
    }
    setEditingAnnotation(null);
  };

  const handleDoubleClick = (e) => {
    if (tool !== 'select' && tool !== 'hand') return;
    const pos = getPos(e);
    const vp = pageOrder.filter(p => !deletedPages.has(p))[currentPage];
    const pageKey = String(vp);
    const pageAnns = annotations?.pages?.[pageKey] || [];
    for (let i = pageAnns.length - 1; i >= 0; i--) {
      const ann = pageAnns[i];
      if (hitTestAnnotation(pos, ann) && ['text', 'flag', 'wallopening'].includes(ann.type)) {
        const editX = ann.x;
        const editY = ann.type === 'text' ? ann.y : ann.y - 28;
        setEditingAnnotation({ pageKey, idx: i, value: ann.text || '', x: editX, y: editY });
        e.stopPropagation();
        return;
      }
    }
  };

  // ─── Page management ──────────────────────────────
  const visiblePages = pageOrder.filter(p => !deletedPages.has(p));

  const deletePage = (pageIdx) => {
    const newDeleted = new Set([...deletedPages, pageIdx]);
    setDeletedPages(newDeleted);
    // Delete annotations for the removed page
    const newAnns = { ...annotations, pages: { ...annotations.pages } };
    delete newAnns.pages[String(pageIdx)];
    setAnnotations(newAnns);
    if (currentPage >= visiblePages.length - 1) setCurrentPage(Math.max(0, visiblePages.length - 2));
    autoSave(newAnns, pageOrder, newDeleted);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...pageOrder];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved);
    setPageOrder(newOrder);
    setDragIdx(idx);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    autoSave(annotations, pageOrder, deletedPages);
  };

  // ─── Complete Submittal ─────────────────────────────
  const handleCompleteSubmittal = async () => {
    if (!selectedSubmittal || !project) return;
    setCompleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      // 1. Save annotations
      await axios.put(
        `${API}/projects/${project.id}/project-submittals/${selectedSubmittal.id}/annotations`,
        { annotations: { ...annotations, page_order: pageOrder, deleted_pages: [...deletedPages] } },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      // 2. Export annotated PDF
      const res = await axios.post(
        `${API}/projects/${project.id}/project-submittals/${selectedSubmittal.id}/export-annotated`,
        { annotations: { ...annotations, page_order: pageOrder, deleted_pages: [...deletedPages] } },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.data.success) {
        toast.success(`Submittal completed: ${res.data.filename}`);
        // 3. Navigate to Submittals tab
        onComplete();
      } else {
        toast.error('Export failed');
      }
    } catch (e) {
      toast.error('Complete failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCompleting(false);
    }
  };

  // ─── Render annotation shape ─────────────────────
  const renderAnnotation = (ann, idx, pageKey, isPreview = false) => {
    const isSelected = selectedAnnotation?.pageKey === pageKey && selectedAnnotation?.idx === idx && !isPreview;
    const handleClick = (e) => {
      if ((tool === 'select' || tool === 'hand') && !isPreview) {
        e.stopPropagation();
        setSelectedAnnotation({ pageKey, idx });
      }
    };
    const isInteractive = tool === 'select' || tool === 'hand';
    const commonProps = {
      key: idx,
      onClick: handleClick,
      onDoubleClick: (e) => {
        if (isInteractive && !isPreview && ['text', 'flag', 'wallopening'].includes(ann.type)) {
          e.stopPropagation();
          setEditingAnnotation({ pageKey, idx, value: ann.text || '', x: ann.x, y: ann.type === 'text' ? ann.y : ann.y - 28 });
        }
      },
      style: { cursor: isInteractive ? 'move' : 'crosshair', pointerEvents: isInteractive ? 'auto' : 'none' },
    };

    switch (ann.type) {
      case 'box':
        return <rect {...commonProps} x={ann.x} y={ann.y} width={Math.abs(ann.width)} height={Math.abs(ann.height)}
          stroke={ann.color} strokeWidth={isSelected ? 3 : ann.strokeWidth} fill="none"
          strokeDasharray={isSelected ? '6 3' : 'none'} />;
      case 'cloud':
        return <path {...commonProps} d={cloudPath(ann.x, ann.y, Math.abs(ann.width), Math.abs(ann.height))}
          stroke={ann.color} strokeWidth={isSelected ? 3 : ann.strokeWidth} fill="none"
          strokeDasharray={isSelected ? '6 3' : 'none'} />;
      case 'arrow':
        return <g {...commonProps}>
          <line x1={ann.x} y1={ann.y} x2={ann.x2} y2={ann.y2}
            stroke={ann.color} strokeWidth={isSelected ? 3 : ann.strokeWidth} markerEnd={`url(#arrowhead-${ann.color.replace('#', '')})`} />
        </g>;
      case 'highlight':
        return <rect {...commonProps} x={ann.x} y={ann.y} width={Math.abs(ann.width)} height={Math.abs(ann.height)}
          fill={ann.color} fillOpacity={0.3} stroke={isSelected ? ann.color : 'none'} strokeWidth={isSelected ? 1 : 0} />;
      case 'underline':
        return <line {...commonProps} x1={ann.x} y1={ann.y + Math.abs(ann.height)} x2={ann.x + Math.abs(ann.width)} y2={ann.y + Math.abs(ann.height)}
          stroke={ann.color} strokeWidth={isSelected ? 3 : ann.strokeWidth} />;
      case 'text':
        return <text {...commonProps} x={ann.x} y={ann.y}
          fill={ann.color} fontSize={ann.fontSize || 14}
          fontFamily={ann.fontFamily || 'Times New Roman'}
          fontWeight="bold"
          stroke={isSelected ? ann.color : 'none'} strokeWidth={isSelected ? 0.3 : 0}
          dominantBaseline="text-before-edge"
          data-testid={`text-ann-${idx}`}>
          {ann.text}
        </text>;
      case 'checkmark':
        return <text {...commonProps} x={ann.x} y={ann.y}
          fill={ann.color} fontSize={18}
          fontWeight="bold"
          dominantBaseline="central"
          textAnchor="middle"
          stroke={isSelected ? ann.color : 'none'} strokeWidth={isSelected ? 0.3 : 0}
          data-testid={`checkmark-ann-${idx}`}>
          &#10003;
        </text>;
      case 'xmark':
        return <text {...commonProps} x={ann.x} y={ann.y}
          fill={ann.color || '#dc2626'} fontSize={20}
          fontWeight="bold"
          dominantBaseline="central"
          textAnchor="middle"
          stroke={isSelected ? (ann.color || '#dc2626') : 'none'} strokeWidth={isSelected ? 0.3 : 0}
          data-testid={`xmark-ann-${idx}`}>
          &#10007;
        </text>;
      case 'bigx': {
        const s = ann.scale || 1;
        const arm = 24 * s;
        const sw = 4 * s;
        return <g {...commonProps} data-testid={`bigx-ann-${idx}`}>
          {isSelected && <circle cx={ann.x} cy={ann.y} r={arm + 4} fill="none"
            stroke="#2563eb" strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />}
          <line x1={ann.x - arm} y1={ann.y - arm} x2={ann.x + arm} y2={ann.y + arm}
            stroke="#2563eb" strokeWidth={sw} strokeLinecap="round" />
          <line x1={ann.x + arm} y1={ann.y - arm} x2={ann.x - arm} y2={ann.y + arm}
            stroke="#2563eb" strokeWidth={sw} strokeLinecap="round" />
        </g>;
      }
      case 'circlexmark':
        return <g {...commonProps} data-testid={`circlexmark-ann-${idx}`}>
          <circle cx={ann.x} cy={ann.y} r={12}
            fill="#fde047" fillOpacity={0.7}
            stroke={isSelected ? '#ca8a04' : '#eab308'} strokeWidth={isSelected ? 2 : 1.5} />
          <text x={ann.x} y={ann.y}
            fill="#b91c1c" fontSize={18} fontWeight="bold"
            dominantBaseline="central" textAnchor="middle"
            style={{ pointerEvents: 'none' }}>
            &#10007;
          </text>
        </g>;
      case 'structhighlight':
        return <line {...commonProps}
          x1={ann.x} y1={ann.y + (ann.height || 0) / 2}
          x2={ann.x + (ann.width || 0)} y2={ann.y + (ann.height || 0) / 2}
          stroke="#4ade80" strokeOpacity={0.45}
          strokeWidth={Math.abs(ann.height) || ann.strokeWidth || 12}
          strokeLinecap="round"
          data-testid={`structhighlight-ann-${idx}`} />;
      case 'wallopening':
      case 'flag': {
        const label = ann.text || 'WALL OPENING REQUIRED';
        const bgColor = ann.bg || '#dc2626';
        const borderColor = ann.border || '#991b1b';
        const fgColor = ann.fg || '#ffffff';
        const charW = 8.2, padX = 14;
        const flagW = label.length * charW + padX * 2;
        const flagH = 28;
        const fx = ann.x, fy = ann.y;
        return <g {...commonProps}
          data-testid={`flag-ann-${idx}`}>
          <line x1={fx} y1={fy} x2={fx} y2={fy + 10} stroke={bgColor} strokeWidth={2} />
          <rect x={fx} y={fy - flagH} width={flagW} height={flagH} rx={3}
            fill={bgColor} stroke={isSelected ? '#fff' : borderColor} strokeWidth={isSelected ? 2 : 1} />
          <text x={fx + flagW / 2} y={fy - flagH / 2}
            fill={fgColor} fontSize={12} fontWeight="bold" fontFamily="Arial, sans-serif"
            textAnchor="middle" dominantBaseline="central"
            style={{ pointerEvents: 'none' }}>
            {label}
          </text>
        </g>;
      }
      default:
        return null;
    }
  };

  // ─── No submittals ──────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (submittals.length === 0) {
    return (
      <Card><CardContent className="py-12 text-center">
        <p className="text-muted-foreground">No built submittal packages found. Build a package in the Submittals step first.</p>
      </CardContent></Card>
    );
  }

  const visiblePage = visiblePages[currentPage];

  return (
    <div className="space-y-3" data-testid="annotate-step">
      {/* Submittal selector */}
      {submittals.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">Submittal:</span>
          {submittals.map(s => (
            <Button key={s.id} size="sm" variant={selectedSubmittal?.id === s.id ? 'default' : 'outline'}
              onClick={() => setSelectedSubmittal(s)} data-testid={`select-submittal-${s.id}`}>
              {s.submittal_name || s.name || 'Submittal'}
            </Button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 flex-wrap bg-card border rounded-lg p-2" data-testid="annotation-toolbar">
        {TOOLS.map(t => t.id === 'flag' ? (
          <div key="flag" className="relative" ref={flagMenuRef}>
            <div className="flex items-center">
              <Button size="sm" variant={tool === 'flag' ? 'default' : 'ghost'}
                onClick={() => { setTool('flag'); setSelectedAnnotation(null); }}
                title={typeof activeFlag === 'object' ? activeFlag.text : activeFlag} data-testid="tool-flag" className="h-8 px-1.5 rounded-r-none">
                <Flag className="h-4 w-4" />
              </Button>
              <Button size="sm" variant={tool === 'flag' ? 'default' : 'ghost'}
                onClick={() => setShowFlagMenu(p => !p)}
                data-testid="flag-dropdown-btn" className="h-8 w-5 p-0 rounded-l-none border-l border-border/40">
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            {showFlagMenu && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-popover border rounded-md shadow-lg min-w-[240px] py-1"
                data-testid="flag-dropdown-menu">
                {FLAG_PRESETS.map(f => (
                  <button key={f.text} onClick={() => { setActiveFlag(f); setTool('flag'); setShowFlagMenu(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs font-medium hover:bg-accent transition-colors flex items-center gap-2 ${(typeof activeFlag === 'object' ? activeFlag.text : activeFlag) === f.text ? 'bg-accent text-accent-foreground' : 'text-popover-foreground'}`}
                    data-testid={`flag-option-${f.text.toLowerCase().replace(/\s+/g, '-')}`}>
                    <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: f.bg }} />
                    {f.text}
                  </button>
                ))}
                <div className="border-t my-1" />
                <div className="px-3 py-1.5 flex gap-1">
                  <input type="text" value={customFlagText} onChange={e => setCustomFlagText(e.target.value.toUpperCase())}
                    placeholder="CUSTOM FLAG TEXT"
                    className="flex-1 text-xs bg-muted border rounded px-2 py-1 text-foreground placeholder:text-muted-foreground"
                    data-testid="custom-flag-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && customFlagText.trim()) {
                        setActiveFlag({ text: customFlagText.trim(), bg: '#dc2626', border: '#991b1b', fg: '#ffffff' });
                        setTool('flag');
                        setShowFlagMenu(false);
                        setCustomFlagText('');
                      }
                    }} />
                  <Button size="sm" variant="default" className="h-7 text-xs px-2"
                    disabled={!customFlagText.trim()}
                    data-testid="custom-flag-add-btn"
                    onClick={() => {
                      if (customFlagText.trim()) {
                        setActiveFlag({ text: customFlagText.trim(), bg: '#dc2626', border: '#991b1b', fg: '#ffffff' });
                        setTool('flag');
                        setShowFlagMenu(false);
                        setCustomFlagText('');
                      }
                    }}>
                    Add
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Button key={t.id} size="sm" variant={tool === t.id ? 'default' : 'ghost'}
            onClick={() => { setTool(t.id); setSelectedAnnotation(null); }}
            title={t.label} data-testid={`tool-${t.id}`}
            className={`h-8 w-8 p-0 ${t.id === 'structhighlight' && tool === t.id ? 'bg-green-600 hover:bg-green-700' : ''}`}>
            <t.icon className={`h-4 w-4 ${t.id === 'structhighlight' && tool !== t.id ? 'text-green-500' : ''}`} />
          </Button>
        ))}

        <div className="w-px h-6 bg-border mx-1" />

        {/* Color picker */}
        <div className="flex items-center gap-1">
          <Palette className="h-3.5 w-3.5 text-muted-foreground" />
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)}
              className={`h-5 w-5 rounded-full border-2 transition-transform ${color === c ? 'scale-125 border-foreground' : 'border-transparent hover:scale-110'}`}
              style={{ backgroundColor: c }} data-testid={`color-${c}`} />
          ))}
        </div>

        <div className="w-px h-6 bg-border mx-1" />

        {/* Font size — visible when text tool selected */}
        {tool === 'text' && (
          <>
            <select
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="h-8 text-xs bg-muted border rounded px-1 text-foreground"
              data-testid="font-size-select"
            >
              {FONT_SIZES.map(s => (
                <option key={s} value={s}>{s}px</option>
              ))}
            </select>
            <div className="w-px h-6 bg-border mx-1" />
          </>
        )}

        {/* Active flag label */}
        {tool === 'flag' && (
          <>
            <span className="text-xs font-semibold px-2 py-1 rounded"
              style={{ color: typeof activeFlag === 'object' ? activeFlag.bg : '#dc2626', backgroundColor: typeof activeFlag === 'object' ? activeFlag.bg + '15' : '#dc262615' }}
              data-testid="active-flag-label">
              {typeof activeFlag === 'object' ? activeFlag.text : activeFlag}
            </span>
            <div className="w-px h-6 bg-border mx-1" />
          </>
        )}

        {/* Structure highlight width control */}
        {tool === 'structhighlight' && (
          <>
            <label className="text-xs text-muted-foreground flex items-center gap-1">
              Width:
              <input type="range" min={4} max={40} value={structHighlightWidth}
                onChange={e => setStructHighlightWidth(Number(e.target.value))}
                className="w-16 h-4" data-testid="struct-highlight-width" />
              <span className="text-xs font-mono w-6">{structHighlightWidth}</span>
            </label>
            <div className="w-px h-6 bg-border mx-1" />
          </>
        )}

        <Button size="sm" variant="ghost" onClick={handleUndo} disabled={history.length === 0}
          title="Undo" data-testid="undo-btn" className="h-8 w-8 p-0">
          <Undo2 className="h-4 w-4" />
        </Button>

        {selectedAnnotation && (
          <>
            <Button size="sm" variant="ghost" onClick={() => handleDeleteAnnotation(selectedAnnotation.pageKey, selectedAnnotation.idx)}
              title="Delete annotation" data-testid="delete-annotation-btn" className="h-8 w-8 p-0 text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
            {(() => {
              const selAnns = annotations?.pages?.[selectedAnnotation.pageKey] || [];
              const selAnn = selAnns[selectedAnnotation.idx];
              if (selAnn?.type === 'bigx') {
                const curScale = selAnn.scale || 1;
                const updateScale = (newScale) => {
                  const clamped = Math.max(0.3, Math.min(5, newScale));
                  const newAnns = { ...annotations };
                  const pageAnns = [...(newAnns.pages?.[selectedAnnotation.pageKey] || [])];
                  pageAnns[selectedAnnotation.idx] = { ...pageAnns[selectedAnnotation.idx], scale: clamped };
                  newAnns.pages = { ...newAnns.pages, [selectedAnnotation.pageKey]: pageAnns };
                  setAnnotations(newAnns);
                  autoSave(newAnns, pageOrder, deletedPages);
                };
                return (
                  <div className="flex items-center gap-0.5 ml-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Scale down"
                      data-testid="bigx-scale-down" onClick={() => updateScale(curScale - 0.25)}>
                      <span className="text-sm font-bold">-</span>
                    </Button>
                    <span className="text-xs font-mono w-10 text-center" data-testid="bigx-scale-label">
                      {Math.round(curScale * 100)}%
                    </span>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Scale up"
                      data-testid="bigx-scale-up" onClick={() => updateScale(curScale + 0.25)}>
                      <span className="text-sm font-bold">+</span>
                    </Button>
                  </div>
                );
              }
              return null;
            })()}
          </>
        )}

        <div className="w-px h-6 bg-border mx-1" />

        <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.max(0.5, s - 0.15))} className="h-8 w-8 p-0" title="Zoom out">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
        <Button size="sm" variant="ghost" onClick={() => setScale(s => Math.min(2, s + 0.15))} className="h-8 w-8 p-0" title="Zoom in">
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setScale(1.0)} className="h-8 px-2 text-xs" title="Fit to page" data-testid="zoom-fit-btn">
          <Maximize className="h-3.5 w-3.5 mr-1" />Fit
        </Button>

        <div className="ml-auto flex items-center gap-2">
          {saving && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Saving...</span>}
        </div>
      </div>

      {/* Main area: thumbnails + page viewer */}
      <div className="flex gap-3" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
        {/* Page Thumbnails Sidebar */}
        <div className="w-28 flex-shrink-0 overflow-y-auto border rounded-lg bg-muted/30 p-1.5 space-y-1.5" data-testid="page-thumbnails">
          {pageOrder.map((pageIdx, orderIdx) => {
            if (deletedPages.has(pageIdx)) return null;
            const visIdx = visiblePages.indexOf(pageIdx);
            const isActive = visIdx === currentPage;
            return (
              <div key={pageIdx}
                draggable onDragStart={() => handleDragStart(orderIdx)}
                onDragOver={(e) => handleDragOver(e, orderIdx)} onDragEnd={handleDragEnd}
                onClick={() => setCurrentPage(visIdx)}
                className={`relative group rounded border-2 cursor-pointer transition-all ${isActive ? 'border-primary ring-1 ring-primary/50' : 'border-transparent hover:border-muted-foreground/30'}`}
                data-testid={`thumbnail-${pageIdx}`}>
                <div className="absolute top-0.5 left-0.5 z-10 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="h-3 w-3 text-muted-foreground cursor-grab" />
                  <span className="text-[9px] bg-background/80 rounded px-0.5">{visIdx + 1}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deletePage(pageIdx); }}
                  className="absolute top-1 right-1 z-10 bg-red-600 hover:bg-red-700 rounded-full p-1 shadow-md transition-transform hover:scale-110"
                  title="Delete page"
                  data-testid={`delete-page-${pageIdx}`}>
                  <Trash2 className="h-3 w-3 text-white" />
                </button>
                <Document file={pdfUrl} loading="" error="">
                  <Page pageNumber={pageIdx + 1} width={90} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
              </div>
            );
          })}
        </div>

        {/* Main Page Viewer */}
        <div ref={containerRef}
          className="flex-1 overflow-auto border rounded-lg bg-muted/20"
          style={{ cursor: tool === 'hand' ? (isPanning ? 'grabbing' : 'grab') : undefined }}
          onMouseDown={tool === 'hand' ? handlePanStart : undefined}
          data-testid="page-viewer">
          {pdfUrl && (
            <div className="relative inline-block" style={{ transform: `scale(${scale})`, transformOrigin: 'top left', margin: '0 auto', display: 'block', width: 'fit-content' }}>
              <Document file={pdfUrl} onLoadSuccess={onDocLoad} loading={<div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
                {visiblePage !== undefined && (
                  <Page pageNumber={visiblePage + 1} renderTextLayer={false} renderAnnotationLayer={false}
                    width={700} />
                )}
              </Document>

              {/* SVG annotation overlay */}
              {visiblePage !== undefined && (
              <svg ref={svgRef}
                className="absolute inset-0 w-full h-full"
                style={{
                  cursor: tool === 'hand' ? 'grab' : tool === 'select' ? 'default' : 'crosshair',
                  pointerEvents: (textInput || editingAnnotation) ? 'none' : 'auto',
                }}
                onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
                onDoubleClick={handleDoubleClick}
                data-testid="annotation-canvas">
                {/* Arrow marker definitions */}
                <defs>
                  {COLORS.map(c => (
                    <marker key={c} id={`arrowhead-${c.replace('#', '')}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill={c} />
                    </marker>
                  ))}
                </defs>

                {/* Saved annotations for current page */}
                {(annotations.pages?.[String(visiblePage)] || []).map((ann, i) => renderAnnotation(ann, i, String(visiblePage)))}

                {/* Drawing preview */}
                {drawing && renderAnnotation(drawing, 'preview', '', true)}
              </svg>
              )}

              {/* Text input overlay */}
              {textInput && visiblePage !== undefined && (
                <div className="absolute" style={{ left: textInput.x, top: textInput.y, zIndex: 20 }}>
                  <input
                    autoFocus
                    type="text"
                    value={textInput.value}
                    onChange={(e) => setTextInput(prev => ({ ...prev, value: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitText(textInput.value);
                      if (e.key === 'Escape') setTextInput(null);
                    }}
                    onBlur={() => commitText(textInput.value)}
                    className="border border-primary bg-white/90 text-black px-1 py-0.5 outline-none min-w-[120px] font-bold"
                    style={{
                      fontFamily: '"Times New Roman", Times, serif',
                      fontSize: `${fontSize}px`,
                      color: color,
                    }}
                    placeholder="Type here..."
                    data-testid="text-input-overlay"
                  />
                </div>
              )}
              {/* Editing annotation text overlay (double-click) */}
              {editingAnnotation && visiblePage !== undefined && (
                <div className="absolute" style={{ left: editingAnnotation.x, top: editingAnnotation.y, zIndex: 20 }}>
                  <input
                    autoFocus
                    type="text"
                    value={editingAnnotation.value}
                    onChange={(e) => setEditingAnnotation(prev => prev ? { ...prev, value: e.target.value } : prev)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitEdit(editingAnnotation.value);
                      if (e.key === 'Escape') setEditingAnnotation(null);
                    }}
                    onBlur={() => commitEdit(editingAnnotation.value)}
                    className="border-2 border-blue-500 bg-white text-black px-1 py-0.5 outline-none min-w-[150px] font-bold shadow-lg"
                    style={{ fontSize: '14px' }}
                    placeholder="Edit text..."
                    data-testid="edit-annotation-input"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Page navigation + Complete */}
      <div className="flex items-center justify-between" data-testid="page-nav">
        <div />
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {visiblePages.length}
            {deletedPages.size > 0 && <span className="text-xs ml-1">({deletedPages.size} deleted)</span>}
          </span>
          <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(visiblePages.length - 1, p + 1))} disabled={currentPage >= visiblePages.length - 1}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button
          onClick={handleCompleteSubmittal}
          disabled={completing || !selectedSubmittal}
          data-testid="complete-submittal-btn"
        >
          {completing ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Completing...</>
          ) : (
            <><CheckCircle className="h-4 w-4 mr-2" /> Complete Submittal</>
          )}
        </Button>
      </div>
    </div>
  );
}
